'use client';
import type { ContextUserType } from '@ui/constants/context';
import React, { useEffect, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import { useMessageUpdateFormContext } from '@ui/contexts/message-update-form-context';
import dynamic from 'next/dynamic';

const MessageUpdateForm = dynamic(
  () => import('./message-update-form').then((module) => module.MessageUpdateForm),
  { ssr: false },
);

interface Props {
  userType: ContextUserType;
  main_post_ref: string;
}

const Portal = memo(
  ({ children, targetRef }: { children: React.ReactNode; targetRef: HTMLDivElement }) => {
    return createPortal(children, targetRef);
  },
);
Portal.displayName = 'MessageUpdatePortal';

export const MessageUpdateFormPortal = memo((props: Props) => {
  const { targetMessage, setTargetMessage } = useMessageUpdateFormContext();
  const initialDisplayRef = useRef<string | null>(null);
  const firstChildRef = useRef<HTMLElement | null>(null);

  // The target is a DOM node owned by a list row. If that row remounts, the
  // node is detached and a portal into it renders nothing at all — the form
  // silently disappears and the row looks untouched. Drop the dead target so
  // the row renders normally and Edit can be opened again.
  const isTargetAttached = Boolean(
    targetMessage?.targetRef &&
      typeof document !== 'undefined' &&
      document.contains(targetMessage.targetRef),
  );

  useEffect(() => {
    if (targetMessage?.targetRef && !isTargetAttached) {
      setTargetMessage(null);
    }
  }, [isTargetAttached, targetMessage, setTargetMessage]);

  useEffect(() => {
    if (targetMessage?.targetRef) {
      const firstChild = targetMessage.targetRef.firstElementChild as HTMLElement;
      if (firstChild && firstChildRef.current !== firstChild) {
        firstChildRef.current = firstChild;
        initialDisplayRef.current = firstChild.style.display;
        firstChild.style.display = 'none';
      }

      return () => {
        if (firstChildRef.current) {
          firstChildRef.current.style.display = initialDisplayRef.current || '';
          firstChildRef.current = null;
        }
      };
    }
  }, [targetMessage]);

  const onDismiss = () => {
    setTargetMessage(null);
    if (firstChildRef.current) {
      firstChildRef.current.style.display = initialDisplayRef.current || '';
    }
    firstChildRef.current = null;
  };

  if (!targetMessage?.targetRef || !isTargetAttached) return null;

  return (
    <Portal targetRef={targetMessage.targetRef}>
      <MessageUpdateForm
        message={targetMessage}
        type={targetMessage.type}
        userType={props.userType}
        onDismiss={onDismiss}
        query={targetMessage.query}
        main_post_ref={props.main_post_ref}
      />
    </Portal>
  );
});

MessageUpdateFormPortal.displayName = 'MessageUpdateFormPortal';
